import { useMemo, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  useCustomer,
  useCustomerStoreScopes,
  useCustomers,
  useDeactivateCustomer,
  useReactivateCustomer,
  useResendCustomerVerification,
  useSetCustomerStoreScope,
} from '../hooks/use-customers'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { ROLES } from '@/shared/constants/roles'

type StatusFilter = 'all' | 'active' | 'deactivated'

export function CustomersPage() {
  const { user, hasRole } = useAuth()
  const { can } = usePermissions()
  const { t } = useTranslation('customers')
  const { t: tCommon } = useTranslation('common')

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [tenantIdInput, setTenantIdInput] = useState(
    user?.tenantId?.toString() ?? '',
  )
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [scopedToStore, setScopedToStore] = useState<string>('all')
  const [pendingDeactivate, setPendingDeactivate] = useState<number | null>(
    null,
  )
  const [pendingReactivate, setPendingReactivate] = useState<number | null>(
    null,
  )

  const tenantId = useMemo(() => {
    if (user?.tenantId) return user.tenantId
    const parsed = Number(tenantIdInput)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [user?.tenantId, tenantIdInput])

  const canManage = can(ACTIONS.CUSTOMER_MANAGE)
  const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN)

  // The roster dropdown needs the tenant's INTERNAL stores independent of any customer, which
  // the restaurant list already carries — MANAGER and ASSISTANT both hold restaurant:view.
  const { data: restaurants } = useRestaurants()
  const internalStores = useMemo(
    () => (restaurants?.items ?? []).filter((r) => r.visibility === 'INTERNAL'),
    [restaurants],
  )

  const { data, isLoading, error } = useCustomers({
    search: search.trim() || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    tenantId,
    scopedToRestaurantId:
      scopedToStore === 'all' ? undefined : Number(scopedToStore),
    enabled: Boolean(tenantId),
  })
  const { data: detail } = useCustomer(selectedId, tenantId)
  const { data: storeScopes, isLoading: storeScopesLoading } =
    useCustomerStoreScopes(selectedId, tenantId)
  const setStoreScope = useSetCustomerStoreScope(selectedId, tenantId)

  const deactivate = useDeactivateCustomer(tenantId)
  const reactivate = useReactivateCustomer(tenantId)
  const resend = useResendCustomerVerification(tenantId)

  const customers = data?.items ?? []

  if (!can(ACTIONS.CUSTOMER_VIEW)) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('permissions.deniedTitle')}</AlertTitle>
        <AlertDescription>
          {t('permissions.deniedDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  const fullName = (c: { firstName: string | null; lastName: string | null }) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || '—'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('title')}</h1>
        <p className="text-muted-foreground mt-1">{t('subtitle')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {isSuperAdmin && !user?.tenantId && (
          <div>
            <Label htmlFor="tenant-id">{t('filters.tenantIdLabel')}</Label>
            <Input
              id="tenant-id"
              type="number"
              value={tenantIdInput}
              onChange={(event) => setTenantIdInput(event.target.value)}
              placeholder={t('filters.tenantIdPlaceholder')}
              className="mt-1.5"
            />
          </div>
        )}
        <div>
          <Label htmlFor="customer-search">{t('filters.searchLabel')}</Label>
          <Input
            id="customer-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="status-filter">{t('filters.statusLabel')}</Label>
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as StatusFilter)}
          >
            <SelectTrigger id="status-filter" className="mt-1.5">
              <SelectValue placeholder={t('filters.statusAll')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.statusAll')}</SelectItem>
              <SelectItem value="active">{t('status.active')}</SelectItem>
              <SelectItem value="deactivated">
                {t('status.deactivated')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* The internal-store roster. Hidden entirely for a tenant that runs no internal
            store, which is every ordinary tenant. */}
        {internalStores.length > 0 && (
          <div>
            <Label htmlFor="scoped-store-filter">
              {t('filters.scopedToStoreLabel')}
            </Label>
            <Select value={scopedToStore} onValueChange={setScopedToStore}>
              <SelectTrigger id="scoped-store-filter" className="mt-1.5">
                <SelectValue placeholder={t('filters.scopedToStoreAll')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('filters.scopedToStoreAll')}
                </SelectItem>
                {internalStores.map((store) => (
                  <SelectItem key={store.id} value={String(store.id)}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!tenantId && isSuperAdmin && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('tenant.requiredTitle')}</AlertTitle>
          <AlertDescription>{t('tenant.requiredDescription')}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tCommon('error')}</AlertTitle>
          <AlertDescription>{t('table.loadFailed')}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">
          {t('table.loading')}
        </div>
      ) : customers.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t('table.empty')}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('table.email')}</TableHead>
              <TableHead>{t('table.name')}</TableHead>
              <TableHead>{t('table.verified')}</TableHead>
              <TableHead>{t('table.status')}</TableHead>
              <TableHead>{t('table.registered')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">{entry.email}</TableCell>
                <TableCell>{fullName(entry)}</TableCell>
                <TableCell>
                  <Badge
                    variant={entry.emailVerified ? 'default' : 'secondary'}
                  >
                    {entry.emailVerified
                      ? t('status.verified')
                      : t('status.unverified')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={entry.deactivated ? 'secondary' : 'default'}>
                    {entry.deactivated
                      ? t('status.deactivated')
                      : t('status.active')}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {entry.createdAt
                    ? new Date(entry.createdAt).toLocaleDateString()
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedId(entry.id)}
                  >
                    {t('table.view')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Sheet
        open={selectedId !== null}
        onOpenChange={(open) => !open && setSelectedId(null)}
      >
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('detail.title')}</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="space-y-4 px-4 pb-6">
              <DetailRow label={t('detail.email')} value={detail.email} />
              <DetailRow label={t('detail.name')} value={fullName(detail)} />
              <DetailRow
                label={t('detail.phone')}
                value={detail.phone ?? '—'}
              />
              <DetailRow
                label={t('detail.birthDate')}
                value={
                  detail.birthDate
                    ? new Date(detail.birthDate).toLocaleDateString()
                    : '—'
                }
              />
              <DetailRow
                label={t('detail.verified')}
                value={
                  detail.emailVerified
                    ? t('status.verified')
                    : t('status.unverified')
                }
              />
              <DetailRow
                label={t('detail.status')}
                value={
                  detail.deactivated
                    ? t('status.deactivated')
                    : t('status.active')
                }
              />
              <DetailRow
                label={t('detail.registered')}
                value={
                  detail.createdAt
                    ? new Date(detail.createdAt).toLocaleString()
                    : '—'
                }
              />
              <DetailRow
                label={t('detail.orderCount')}
                value={String(detail.orderCount)}
              />
              <DetailRow
                label={t('detail.loyaltyPoints')}
                value={String(detail.loyaltyPoints)}
              />

              {/* Internal-store access. Absent for tenants with no INTERNAL store, so the
                  sheet is unchanged for everyone who does not run one. */}
              {(storeScopesLoading || (storeScopes?.items.length ?? 0) > 0) && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-sm font-medium">
                        {t('detail.storeScopesTitle')}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('detail.storeScopesDescription')}
                      </p>
                    </div>
                    {storeScopesLoading ? (
                      <p className="text-sm text-muted-foreground">
                        {t('detail.storeScopesLoading')}
                      </p>
                    ) : (
                      (storeScopes?.items ?? []).map((scope) => (
                        <div
                          key={scope.restaurantId}
                          className="flex items-center justify-between gap-4"
                        >
                          <div>
                            <div className="text-sm font-medium">
                              {scope.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {scope.granted && scope.grantedAt
                                ? t('detail.storeScopesGrantedAt', {
                                    date: new Date(
                                      scope.grantedAt,
                                    ).toLocaleDateString(),
                                  })
                                : t('detail.storeScopesNotGranted')}
                            </div>
                          </div>
                          <Switch
                            checked={scope.granted}
                            // Read-only without customer:manage, so an ASSISTANT can audit
                            // access without being able to change it.
                            disabled={!canManage || setStoreScope.isPending}
                            onCheckedChange={(granted) =>
                              setStoreScope.mutate({
                                restaurantId: scope.restaurantId,
                                granted,
                              })
                            }
                            aria-label={scope.name}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {canManage && (
                <>
                  <Separator />
                  <div className="flex flex-col gap-2">
                    {detail.deactivated ? (
                      <Button
                        variant="outline"
                        onClick={() => setPendingReactivate(detail.id)}
                      >
                        {t('actions.reactivate')}
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        onClick={() => setPendingDeactivate(detail.id)}
                      >
                        {t('actions.deactivate')}
                      </Button>
                    )}
                    {!detail.emailVerified && (
                      <Button
                        variant="outline"
                        onClick={() => resend.mutate(detail.id)}
                        disabled={resend.isPending}
                      >
                        {t('actions.resendVerification')}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={pendingDeactivate !== null}
        onOpenChange={(open) => !open && setPendingDeactivate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm.deactivateTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirm.deactivateDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeactivate !== null) {
                  deactivate.mutate(pendingDeactivate)
                  setPendingDeactivate(null)
                }
              }}
            >
              {t('confirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingReactivate !== null}
        onOpenChange={(open) => !open && setPendingReactivate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm.reactivateTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirm.reactivateDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingReactivate !== null) {
                  reactivate.mutate(pendingReactivate)
                  setPendingReactivate(null)
                }
              }}
            >
              {t('confirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  )
}
